import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, IconButton, Tooltip, Button, Skeleton, useTheme } from "@mui/material";
import {
  ZoomIn,
  ZoomOut,
  FitScreen,
  Fullscreen,
  FullscreenExit,
  ErrorOutline,
} from "@mui/icons-material";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Set the worker to react-pdf's internal pdfjs version (4.3.136) as there are multiple versions of pdfjs in the project
pdfjs.GlobalWorkerOptions.workerSrc =
  "https://cdn.jsdelivr.net/npm/pdfjs-dist@4.3.136/build/pdf.worker.min.mjs";

const PdfViewer = ({ fileUrl, onScrolledToBottom, fillHeight = false }) => {
  const theme = useTheme();
  const scrollRef = useRef();
  const pageRefs = useRef([]);
  const fullScreenRef = useRef(null);

  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasReachedBottom, setHasReachedBottom] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [pageWidth, setPageWidth] = useState(960);

  const customColors = theme.customColors || {};
  const borderSubtle = customColors.border?.subtle ?? "#E5E7EB";
  const borderMain = customColors.border?.main ?? "#E8EAED";
  const textSecondary = customColors.text?.secondary ?? "#6B7280";
  const textStrong = customColors.text?.strong ?? "#374151";

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const updateWidth = () => {
      if (el.clientWidth > 0) {
        const padding = 80;
        setPageWidth(Math.min(960, Math.max(280, el.clientWidth - padding)));
      }
    };
    updateWidth();
    const ro = new ResizeObserver(updateWidth);
    ro.observe(el);
    return () => ro.disconnect();
  }, [numPages]);

  const loadingNode = (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        minHeight: 280,
        gap: 2,
        px: 2,
        py: 3,
      }}
    >
      <Skeleton
        variant="text"
        width="60%"
        height={28}
        sx={{ bgcolor: "grey.300" }}
      />
      <Skeleton
        variant="text"
        width="40%"
        height={20}
        sx={{ bgcolor: "grey.300" }}
      />
      <Skeleton
        variant="rectangular"
        height={120}
        sx={{ bgcolor: "grey.300", borderRadius: 1, width: "100%" }}
      />
      <Skeleton variant="text" width="100%" height={20} sx={{ bgcolor: "grey.300" }} />
      <Skeleton variant="text" width="90%" height={20} sx={{ bgcolor: "grey.300" }} />
      <Skeleton variant="text" width="95%" height={20} sx={{ bgcolor: "grey.300" }} />
      <Skeleton
        variant="rectangular"
        height={200}
        sx={{ bgcolor: "grey.300", borderRadius: 1, width: "100%", mt: 1 }}
      />
    </Box>
  );

  const errorNode = (
    <Box
      role="alert"
      aria-live="polite"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        minHeight: "60vh",
        width: "100%",
        p: 2,
      }}
    >
      <Box
        sx={{
          backgroundColor: customColors.background?.tableRow ?? "#F3F4F6",
          boxShadow: customColors.shadow?.card ?? "0 1px 3px rgba(0,0,0,0.1)",
          border: `1px solid ${borderMain}`,
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 220,
          maxWidth: 520,
          width: "100%",
          px: 3,
          py: 4,
          gap: 2,
        }}
      >
        <ErrorOutline
          sx={{
            color: customColors.icons?.secondary ?? textSecondary,
            fontSize: 40,
            mb: 0.5,
          }}
          aria-hidden
        />
        <Typography variant="h6" sx={{ textAlign: "center" }}>
          Couldn&apos;t load this document
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "center", maxWidth: 360 }}>
          The file may be missing or unavailable. Try again in a moment.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setRetryKey((k) => k + 1)}
          aria-label="Try loading the document again"
          sx={{
            mt: 1,
            textTransform: "none",
            px: 3,
            py: 1.5,
            fontWeight: 600,
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: theme.customColors?.button?.hover,
            },
            "&:disabled": {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
            },
          }}
        >
          Try again
        </Button>
      </Box>
    </Box>
  );

  const toggleFullscreen = () => {
    const container = fullScreenRef.current;
    if (!document.fullscreenElement) {
      container.requestFullscreen().catch((err) => {
        console.error("Error attempting fullscreen:", err);
      });
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.1, 2.0));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.1, 0.5));
  };

  const handleResetZoom = () => {
    setScale(1.0);
  };

  useEffect(() => {
    if (!scrollRef.current || !pageRefs.current.length) return;

    const scrollElement = scrollRef.current;

    const handleVisiblePage = () => {
      const containerTop = scrollElement.scrollTop;
      const containerHeight = scrollElement.clientHeight;
      const centerY = containerTop + containerHeight / 2;

      let closestPage = null;
      let closestDistance = Infinity;

      pageRefs.current.forEach((ref, index) => {
        if (!ref) return;
        const pageTop = ref.offsetTop;
        const pageHeight = ref.offsetHeight;
        const pageCenter = pageTop + pageHeight / 2;
        const distance = Math.abs(centerY - pageCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestPage = index + 1;
        }
      });

      if (closestPage !== currentPage) {
        setCurrentPage(closestPage);
      }
    };

    // Listen to scroll
    scrollElement.addEventListener("scroll", handleVisiblePage);

    // Re-check on zoom/resize
    window.addEventListener("resize", handleVisiblePage);
    handleVisiblePage(); // initial call

    return () => {
      scrollElement.removeEventListener("scroll", handleVisiblePage);
      window.removeEventListener("resize", handleVisiblePage);
    };
  }, [numPages, scale, currentPage]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        document.activeElement.tagName === "INPUT" ||
        document.activeElement.tagName === "TEXTAREA"
      )
        return;
      if (e.key === "f" || e.key === "F") {
        toggleFullscreen();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        maxWidth: fillHeight ? "100%" : "1000px",
        margin: fillHeight ? 0 : "0 auto",
        height: fillHeight ? "100%" : "auto",
      }}
    >
      <Box
        ref={fullScreenRef}
        sx={{
          position: "relative",
          height: isFullScreen ? "100vh" : fillHeight ? "100%" : "500px",
          border: "none",
          borderRadius: isFullScreen ? 0 : "12px",
          overflowY: "hidden",
          overflowX: "hidden",
          bgcolor: isFullScreen ? "#111827" : (customColors.background?.tableRow ?? "#F3F4F6"),
          marginY: fillHeight ? 0 : 4,
          boxShadow: "none",
        }}
      >
        {/* Toolbar */}
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10,
            bgcolor: "rgb(230, 230, 230, 0.85)",
            borderRadius: "12px",
            px: 2,
            py: 0.5,
            display: "flex",
            alignItems: "center",
            gap: 3,
          }}
        >
          <Tooltip title="Zoom Out">
            <IconButton onClick={handleZoomOut} size="small">
              <ZoomOut />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom In">
            <IconButton onClick={handleZoomIn} size="small">
              <ZoomIn />
            </IconButton>
          </Tooltip>
          <Typography variant="body2">{Math.round(scale * 100)}%</Typography>
          <Tooltip title="Reset Zoom">
            <IconButton onClick={handleResetZoom} size="small">
              <FitScreen />
            </IconButton>
          </Tooltip>
          <Tooltip title="Toggle Fullscreen">
            <IconButton onClick={toggleFullscreen} size="small">
              {isFullScreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Scrollable PDF Area */}
        <Box
          ref={scrollRef}
          onScroll={(e) => {
            const el = e.target;
            if (
              el.scrollTop + el.clientHeight >= el.scrollHeight - 10 &&
              !hasReachedBottom
            ) {
              setHasReachedBottom(true);
              if (onScrolledToBottom) onScrolledToBottom(); // Notify parent ONCE
            }
          }}
          sx={{
            height: "100%",
            width: "100%",
            overflowY: "auto",
            backgroundColor: "#fff",
            scrollbarWidth: "thin",
            scrollbarColor: "#9CA3AF transparent",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-button": {
              display: "none",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-corner": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "transparent",
              // backgroundColor: "#9CA3AF",
              // borderRadius: "999px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#6B7280",
            },
          }}
        >
          <Box
            sx={{
              transform: `scale(${scale})`,
              transformOrigin: "top center",
              margin: "auto",
              width: "100%",
              maxWidth: "100%",
              height: scale < 1 ? `${100 / scale}%` : "auto",
            }}
          >
            <Box
              sx={{
                borderRadius: "12px",
                padding: { xs: 2, sm: "32px 32px 40px" },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                overflow: "hidden",
                width: "100%",
                maxWidth: 960,
                boxSizing: "border-box",
                // Force react-pdf root and message wrappers to full width so loading/error nodes don't shrink-wrap
                "& .react-pdf__Document": {
                  width: "100%",
                  display: "block",
                },
                "& .react-pdf__message": {
                  width: "100%",
                  display: "block",
                },
              }}
            >
              <Document
                key={retryKey}
                file={fileUrl}
                loading={loadingNode}
                error={errorNode}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={(error) => console.error("PDF load error:", error)}
              >
                {(pageRefs.current = [])}
                {Array.from(new Array(numPages), (_, index) => (
                  <Box
                    key={`page_${index + 1}`}
                    ref={(el) => (pageRefs.current[index] = el)}
                    data-page={index + 1}
                    sx={{
                      "&:not(:last-of-type)": {
                        borderBottom: `1px solid ${borderSubtle}`,
                        pb: 3,
                        mb: 3,
                      },
                    }}
                  >
                    <Page pageNumber={index + 1} scale={1} width={pageWidth} />
                  </Box>
                ))}
              </Document>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PdfViewer;
